/**
 * Avatar Editor Store
 * Manages avatar style selection, options, and combinations
 */

import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { Avatar } from '@dicebear/core';
import {
  describeStyleOptions,
  generateRandomOptions,
  toDicebearOptions,
  type StyleOptions,
} from 'modules/Avatar/services/avatarOptionDescriptor';
import {
  avatarStyleKeys,
  getStyle,
  isAvatarStyleKey,
  loadAllStyles,
  loadStyle,
  type AvatarStyleKey,
} from 'modules/Avatar/services/dicebearStyleRegistry';
import {
  generateAvatarCombinations,
  type AvatarCombinations,
} from 'modules/Avatar/services/avatarCombinations';

export type StyleName = AvatarStyleKey;

const DEFAULT_STYLE: StyleName = 'avataaars';

/**
 * Storage keys are versioned: DiceBear 10 renamed every component option
 * (`eyes` -> `eyesVariant`) and validates strictly, so v9 selections cannot be
 * replayed — the editor starts from fresh random options instead.
 */
const STYLE_STORAGE_KEY = 'avatar_editor_style_v10';
const OPTIONS_STORAGE_KEY = 'avatar_editor_options_v10_1';
const SEED_STORAGE_KEY = 'avatar_editor_seed_v10';

const DEFAULT_SEED = 'Felix';

const randomSeed = () => Math.random().toString(36).substring(2, 12);

/** Descriptors are derived from a style definition, so they never change. */
const descriptorCache = new Map<StyleName, StyleOptions>();

function describeStyle(styleName: StyleName): StyleOptions {
  const cached = descriptorCache.get(styleName);
  if (cached) return cached;

  const style = getStyle(styleName);
  if (!style) return {};

  const descriptor = describeStyleOptions(style);
  descriptorCache.set(styleName, descriptor);
  return descriptor;
}

export const useAvatarStore = defineStore('avatar', () => {
  const selectedStyleName = useLocalStorage<StyleName>(
    STYLE_STORAGE_KEY,
    DEFAULT_STYLE,
  );

  // Guard against a style key that no longer exists (or a v9 camelCase key).
  if (!isAvatarStyleKey(selectedStyleName.value)) {
    selectedStyleName.value = DEFAULT_STYLE;
  }

  // All style options (persisted)
  const allStyleOptions = useLocalStorage<
    Record<string, Record<string, string>>
  >(OPTIONS_STORAGE_KEY, {});

  // Text-based styles (initials, initial-face) render nothing without a seed,
  // and it decides any component the user has not picked explicitly.
  const seed = useLocalStorage<string>(SEED_STORAGE_KEY, DEFAULT_SEED);

  // Styles whose definitions have finished loading
  const loadedStyleNames = ref<StyleName[]>([]);

  // Active tab, keyed by option name so it survives groups appearing and
  // disappearing as the selection changes.
  const selectedTab = ref('style');

  /** Seed random selections the first time a style becomes available. */
  function ensureOptions(styleName: StyleName) {
    if (allStyleOptions.value[styleName]) return;
    allStyleOptions.value[styleName] = generateRandomOptions(
      describeStyle(styleName),
    );
  }

  async function ensureStyleLoaded(styleName: StyleName) {
    const style = await loadStyle(styleName);
    if (!style) return;
    ensureOptions(styleName);
    if (!loadedStyleNames.value.includes(styleName)) {
      loadedStyleNames.value.push(styleName);
    }
  }

  // Load the selected style first so the preview appears without waiting on the
  // full ~2.2MB of style definitions, then fill in the picker grid.
  async function initialize() {
    await ensureStyleLoaded(selectedStyleName.value);
    await loadAllStyles();
    for (const styleName of avatarStyleKeys) {
      await ensureStyleLoaded(styleName);
    }
  }

  void initialize();

  // Current style's options
  const currentStyleOptions = computed({
    get: () => allStyleOptions.value[selectedStyleName.value] || {},
    set: (value) => {
      allStyleOptions.value[selectedStyleName.value] = value;
    },
  });

  // Current style definition (undefined until its JSON has loaded)
  const currentStyle = computed(() => getStyle(selectedStyleName.value));

  const isReady = computed(() => currentStyle.value !== undefined);

  // Current style parsed options
  const currentStyleSchema = computed(() =>
    currentStyle.value ? describeStyle(selectedStyleName.value) : {},
  );

  // Current avatar preview
  const currentAvatar = computed(() => {
    const style = currentStyle.value;
    if (!style) return null;

    return new Avatar(style, {
      seed: seed.value,
      ...toDicebearOptions(currentStyleOptions.value, currentStyleSchema.value),
    });
  });

  // Available style combinations for grid
  const styleCombinations = computed(() => {
    const result: Record<
      string,
      Array<{
        active: boolean;
        avatar: Avatar;
        options: Record<string, string>;
      }>
    > = {};

    for (const styleName of loadedStyleNames.value) {
      const style = getStyle(styleName);
      if (!style) continue;

      const options = allStyleOptions.value[styleName] || {};
      const styleOptions = describeStyle(styleName);

      result[styleName] = [
        {
          active: selectedStyleName.value === styleName,
          avatar: new Avatar(style, {
            seed: seed.value,
            ...toDicebearOptions(options, styleOptions),
          }),
          options,
        },
      ];
    }

    return result;
  });

  // Current style option combinations
  const optionCombinations = computed((): AvatarCombinations => {
    const style = currentStyle.value;
    if (!style) return {};

    return generateAvatarCombinations(
      style,
      currentStyleSchema.value,
      currentStyleOptions.value,
      seed.value,
    );
  });

  /**
   * The tab actually shown. An option group disappears once its choices all
   * look the same, so the stored tab can stop existing — fall back to the style
   * picker rather than rendering an empty panel.
   */
  const activeTab = computed({
    get: () =>
      selectedTab.value === 'style' ||
      selectedTab.value in optionCombinations.value
        ? selectedTab.value
        : 'style',
    set: (value: string) => {
      selectedTab.value = value;
    },
  });

  // Actions
  function changeStyle(styleName: string) {
    if (!isAvatarStyleKey(styleName)) {
      console.warn(`Unknown avatar style "${styleName}"`);
      return;
    }

    selectedStyleName.value = styleName;
    selectedTab.value = 'style';
    void ensureStyleLoaded(styleName);
  }

  function updateOptions(options: Record<string, string>) {
    currentStyleOptions.value = options;
  }

  function randomize() {
    seed.value = randomSeed();
    currentStyleOptions.value = generateRandomOptions(currentStyleSchema.value);
  }

  // Scroll to top when tab changes
  watch(selectedTab, () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  return {
    // State
    selectedStyleName,
    seed,
    selectedTab: activeTab,
    currentStyleOptions,
    currentAvatar,
    styleCombinations,
    optionCombinations,
    isReady,
    availableStyles: computed(() => avatarStyleKeys),

    // Actions
    changeStyle,
    updateOptions,
    randomize,
  };
});
